import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, ShieldAlert } from 'lucide-react';
import { isPasswordCompliant } from '@erp/shared';
import { FormField } from '@/components/common/form-field';
import { PasswordInput } from '@/components/common/password-input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSession } from '@/features/auth';
import { useChangePassword } from '@/features/users/api/use-security';
import { PasswordStrength } from '@/features/users/components/password-strength';

/**
 * Blockiert die Anwendung, solange ein administrativ gesetztes Initialpasswort
 * aktiv ist. Erst nach dem Wechsel wird die Oberfläche freigegeben.
 */
export function ForcePasswordChange(): JSX.Element | null {
  const { t } = useTranslation();
  const session = useSession();
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  if (session.data === null || session.data === undefined || !session.data.forcePasswordChange) {
    return null;
  }

  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    currentPassword.length > 0 && isPasswordCompliant(newPassword) && passwordsMatch;

  const submit = (): void => {
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          toast.success(t('security.password.changed'));
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        },
        onError: (error) => toast.error(error.message),
      },
    );
  };

  return (
    <Dialog open>
      <DialogContent
        className="max-w-md"
        closeLabel={t('common.close')}
        hideClose
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" aria-hidden="true" />
            {t('security.forceChange.title')}
          </DialogTitle>
          <DialogDescription>{t('security.forceChange.description')}</DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <FormField id="force-current-password" label={t('security.password.current')} required>
            <PasswordInput
              id="force-current-password"
              autoComplete="current-password"
              autoFocus
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </FormField>
          <FormField id="force-new-password" label={t('security.password.new')} required>
            <PasswordInput
              id="force-new-password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </FormField>
          <PasswordStrength password={newPassword} />
          <FormField
            id="force-confirm-password"
            label={t('security.password.confirm')}
            required
            error={
              confirmPassword.length > 0 && !passwordsMatch
                ? t('security.password.mismatch')
                : undefined
            }
          >
            <PasswordInput
              id="force-confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </FormField>

          <Button type="submit" disabled={!canSubmit || changePassword.isPending}>
            {changePassword.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('security.password.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
